import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, Calendar, Eye, EyeOff, Image } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';
import AdminConfirmDialog from '@/components/admin/AdminConfirmDialog';
import toast from 'react-hot-toast';

const AdminDropshopPage: React.FC = () => {
  const collections = useAdminStore((s) => s.dropshopCollections);
  const deleteCollection = useAdminStore((s) => s.deleteDropshopCollection);
  const { localize } = useTranslation();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleDelete = (): void => {
    if (!deleteId) return;
    deleteCollection(deleteId);
    toast.success('Collection deleted');
    setDeleteId(null);
  };

  const formatDate = (date: string): string => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const active = collections.filter((c) => c.isActive);
  const inactive = collections.filter((c) => !c.isActive);

  const renderGroup = (title: string, items: typeof collections): React.ReactNode => {
    if (items.length === 0) return null;
    return (
      <div>
        <h3 className="text-[10px] font-bold uppercase tracking-[0.3em] text-black/30 dark:text-white/30 mb-3">{title}</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <AnimatePresence mode="popLayout">
            {items.map((col) => {
              const artistName = typeof col.artist === 'string'
                ? col.artist
                : localize(col.artist?.name) || '—';
              return (
                <motion.div
                  key={col._id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="group bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl overflow-hidden hover:border-black/10 dark:hover:border-white/10 transition-all"
                >
                  <div className="flex">
                    <div className="w-32 h-32 shrink-0 bg-black/[0.02] dark:bg-white/[0.02] overflow-hidden">
                      {col.coverImage ? (
                        <img src={col.coverImage} alt={col.title.en} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image size={20} className="text-black/10 dark:text-white/10" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 p-4 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="text-[13px] font-bold truncate">{col.title.en || col.title.ka || 'Untitled'}</h4>
                        <div className="flex items-center gap-1 shrink-0">
                          <Link
                            to={`/admin/dropshop/${col._id}`}
                            className="w-7 h-7 rounded-lg flex items-center justify-center text-black/20 dark:text-white/20 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.04] transition-all"
                          >
                            <Pencil size={12} />
                          </Link>
                          <button
                            onClick={() => setDeleteId(col._id)}
                            className="w-7 h-7 rounded-lg flex items-center justify-center text-black/20 dark:text-white/20 hover:text-red-500 hover:bg-red-500/5 transition-all"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                      <div className="mt-2 space-y-1.5">
                        <div className="flex items-center gap-1.5 text-[10px] text-black/40 dark:text-white/40">
                          <span className="font-medium">{artistName}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-[10px] text-black/40 dark:text-white/40">
                          <Calendar size={10} />
                          <span>{formatDate(col.releaseDate)}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1 text-[10px] text-black/40 dark:text-white/40">
                            {col.isActive ? <Eye size={10} /> : <EyeOff size={10} />}
                            {col.isActive ? 'Active' : 'Inactive'}
                          </span>
                          <span className="text-[10px] text-black/30 dark:text-white/30">
                            {col.artworkIds?.length || 0} artworks · {col.images?.length || 0} images
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">Dropshop</h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">{collections.length} collections</p>
        </div>
        <Link
          to="/admin/dropshop/new"
          className="flex items-center gap-2 px-5 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity"
        >
          <Plus size={14} /> New Collection
        </Link>
      </div>

      {renderGroup('Active Collections', active)}
      {renderGroup('Inactive', inactive)}

      {collections.length === 0 && (
        <div className="text-center py-16">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">No dropshop collections found</p>
        </div>
      )}

      <AdminConfirmDialog
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Collection"
        message="This will permanently remove this dropshop collection. This action cannot be undone."
      />
    </div>
  );
};

export default AdminDropshopPage;
